import React, { lazy, useEffect, useRef } from 'react';
import { BrowserRouter as Router, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { useAuth } from './contexts/AuthContext';
import './i18n';
import SeoPublicRouter from './components/seo/SeoPublicRouter';
import LocaleLayout from './components/LocaleLayout';
import { buildLocalizedPath, resolveInitialLocale, useCurrentLocale } from './lib/localeRouting';
import { trackPageView } from './lib/firebase';
import i18n from './i18n';

const Home = lazy(() => import('./components/home'));
const Subjects = lazy(() => import('./components/subjects'));
const Tutor = lazy(() => import('./components/tutor'));
const Profile = lazy(() => import('./components/profile'));
const Courses = lazy(() => import('./components/coursepage'));
const VideoPlayer = lazy(() => import('./components/videopage'));
const Grades = lazy(() => import('./components/grades'));
const Reels = lazy(() => import('./components/reels'));
const UploadModal = lazy(() => import('./components/uploadmodal'));

const initialLocale = resolveInitialLocale(i18n.language);

function LegacyRedirect({ routeKey = '' }) {
  return <Navigate to={buildLocalizedPath(initialLocale, routeKey)} replace />;
}

function AuthLoadingScreen() {
  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#000',
        color: '#fff',
        padding: '24px',
      }}
    >
      <div
        style={{
          width: '44px',
          height: '44px',
          borderRadius: '999px',
          border: '4px solid rgba(255,255,255,0.15)',
          borderTopColor: '#ef4444',
          animation: 'spin 1s linear infinite',
        }}
      />
    </div>
  );
}

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  const locale = useCurrentLocale();

  if (loading) {
    return <AuthLoadingScreen />;
  }

  if (!user) {
    return <Navigate to={buildLocalizedPath(locale)} replace />;
  }

  return children;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<LegacyRedirect />} />
      <Route path="/courses" element={<LegacyRedirect routeKey="courses" />} />
      <Route path="/video" element={<LegacyRedirect routeKey="video" />} />
      <Route path="/tutor" element={<LegacyRedirect routeKey="tutor" />} />
      <Route path="/profile" element={<LegacyRedirect routeKey="profile" />} />
      <Route path="/subjects" element={<LegacyRedirect routeKey="subjects" />} />
      <Route path="/grades" element={<LegacyRedirect routeKey="grades" />} />
      <Route path="/reels" element={<LegacyRedirect routeKey="reels" />} />
      <Route path="/upload" element={<LegacyRedirect routeKey="upload" />} />

      <Route path="/:locale" element={<LocaleLayout />}>
        <Route index element={<Home />} />
        <Route path="courses" element={<Courses />} />
        <Route path="video" element={<VideoPlayer />} />
        <Route path="tutor" element={<Tutor />} />
        <Route
          path="profile"
          element={(
            <ProtectedRoute>
              <Profile />
            </ProtectedRoute>
          )}
        />
        <Route path="subjects" element={<Subjects />} />
        <Route path="grades" element={<Grades />} />
        <Route path="reels" element={<Reels />} />
        <Route
          path="upload"
          element={(
            <ProtectedRoute>
              <UploadModal onClose={() => window.history.back()} />
            </ProtectedRoute>
          )}
        />
        <Route path="*" element={<SeoPublicRouter />} />
      </Route>

      <Route path="*" element={<LegacyRedirect />} />
    </Routes>
  );
}

function AnalyticsRouteTracker() {
  const location = useLocation();
  const lastTrackedPath = useRef('');

  useEffect(() => {
    const routeKey = `${location.pathname}${location.search}`;

    if (lastTrackedPath.current === routeKey) {
      return;
    }

    lastTrackedPath.current = routeKey;
    trackPageView(location.pathname, location.search);
  }, [location.pathname, location.search]);

  return null;
}

function App() {
  return (
    <Router>
      <AnalyticsRouteTracker />
      <AuthProvider>
        <AppRoutes />
      </AuthProvider>
    </Router>
  );
}

export default App;
